import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/cn';
import { DEVICE_GROUPS, matchDevice } from '@/lib/device-presets';
import type { Viewport } from '@personalizehq/schemas/ir';
import { ChevronDown, Moon, Sun } from 'lucide-react';

// The toggles flip the RENDERER's own token resolution and solver inputs — this is not a preview
// mode, it is the same code path the device runs. `font_scale` is the differentiator (a 1.5× system
// font is where the customer bug reports come from, and neither competitor shows it at all), so it
// sits in the toolbar rather than behind a menu: an author should trip over it.
//
// The scales and the RTL cell are the conformance matrix's (`packages/schemas/src/viewports.ts`);
// the device list is local, because VIEWPORTS is one size by design — it varies scheme/scale/dir.

const SCALES = [1, 1.5, 2] as const;

interface ViewportToolbarProps {
  viewport: Viewport;
  onChange: (viewport: Viewport) => void;
  contentHeight?: number | null;
}

export function ViewportToolbar({ viewport, onChange, contentHeight }: ViewportToolbarProps): React.JSX.Element {
  const set = (patch: Partial<Viewport>): void => onChange({ ...viewport, ...patch });

  return (
    <div className="flex w-max shrink-0 items-center gap-2 text-[12px]">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Device"
            className="flex h-7 shrink-0 items-center gap-1.5 rounded-md border bg-background px-2 hover:bg-secondary"
          >
            <span className="font-medium">{matchDevice(viewport.w, viewport.h)}</span>
            <span className="tabular-nums text-muted-foreground">
              {viewport.w} × {viewport.h}
            </span>
            <ChevronDown className="size-3.5 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="max-h-[70vh] w-56 overflow-y-auto">
          {DEVICE_GROUPS.map((group) => (
            <div key={group.group}>
              <DropdownMenuLabel className="text-[11px] text-muted-foreground">{group.group}</DropdownMenuLabel>
              {group.devices.map((device) => (
                <DropdownMenuItem
                  key={device.name}
                  onSelect={() => set({ w: device.w, h: device.h })}
                  className="flex justify-between gap-6 text-[12px]"
                >
                  <span>{device.name}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {device.w} × {device.h}
                  </span>
                </DropdownMenuItem>
              ))}
            </div>
          ))}
          <DropdownMenuSeparator />
          <div className="grid grid-cols-2 gap-2 p-2">
            <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
              Width
              <Input
                type="number"
                aria-label="Width"
                value={viewport.w}
                min={200}
                max={2048}
                onChange={(event) => {
                  const w = clampDim(event.target.value);
                  if (w !== null) set({ w });
                }}
                onKeyDown={(event) => event.stopPropagation()}
                className="h-7"
              />
            </label>
            <label className="flex flex-col gap-1 text-[11px] text-muted-foreground">
              Height
              <Input
                type="number"
                aria-label="Height"
                value={viewport.h}
                min={200}
                max={2048}
                onChange={(event) => {
                  const h = clampDim(event.target.value);
                  if (h !== null) set({ h });
                }}
                onKeyDown={(event) => event.stopPropagation()}
                className="h-7"
              />
            </label>
            <label className="col-span-2 flex flex-col gap-1 text-[11px] text-muted-foreground">
              Content Height
              <Input
                type="text"
                aria-label="Content height"
                readOnly
                disabled
                value={contentHeight != null ? `${Math.round(contentHeight)}px` : '—'}
                className="h-7"
              />
            </label>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <Group>
        <Toggle
          active={viewport.color_scheme === 'light'}
          aria-label="Light"
          onClick={() => set({ color_scheme: 'light' })}
        >
          <Sun className="size-3.5" />
        </Toggle>
        <Toggle
          active={viewport.color_scheme === 'dark'}
          aria-label="Dark"
          onClick={() => set({ color_scheme: 'dark' })}
        >
          <Moon className="size-3.5" />
        </Toggle>
      </Group>

      <div className="hidden xl:block">
        <Group>
          {SCALES.map((scale) => (
            <Toggle
              key={scale}
              active={viewport.font_scale === scale}
              aria-label={`Font scale ${scale.toFixed(1)}`}
              onClick={() => set({ font_scale: scale })}
            >
              {scale.toFixed(1)}×
            </Toggle>
          ))}
        </Group>
      </div>
      <select
        aria-label="Font scale"
        value={viewport.font_scale}
        onChange={(event) => set({ font_scale: Number(event.target.value) as Viewport['font_scale'] })}
        className="h-7 rounded-md border bg-background px-2 text-[11px] xl:hidden"
      >
        {SCALES.map((scale) => (
          <option key={scale} value={scale}>
            {scale.toFixed(1)}× text
          </option>
        ))}
      </select>

      <div className="hidden xl:block">
        <Group>
          {(['ltr', 'rtl'] as const).map((direction) => (
            <Toggle
              key={direction}
              active={(viewport.direction ?? 'ltr') === direction}
              onClick={() => set({ direction })}
            >
              {direction.toUpperCase()}
            </Toggle>
          ))}
        </Group>
      </div>
      <select
        aria-label="Text direction"
        value={viewport.direction ?? 'ltr'}
        onChange={(event) => set({ direction: event.target.value as 'ltr' | 'rtl' })}
        className="h-7 rounded-md border bg-background px-2 text-[11px] xl:hidden"
      >
        <option value="ltr">LTR</option>
        <option value="rtl">RTL</option>
      </select>
    </div>
  );
}

// ponytail: clamp on every change. Presets are the primary path; typing "3" briefly clamping to 200
// in the secondary custom field is not worth a blur-buffer state machine.
function clampDim(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n === 0) return null;
  return Math.max(200, Math.min(2048, Math.round(n)));
}

function Group({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <div className="flex shrink-0 items-center gap-0.5 rounded-lg border p-0.5">{children}</div>;
}

function Toggle({
  active,
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active: boolean }): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={cn(
        'flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded-md px-2 transition-colors',
        active ? 'bg-secondary font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'
      )}
      {...props}
    >
      {children}
    </button>
  );
}
